import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  FaArrowRight, FaLightbulb, FaStar, FaHeart, FaQuoteLeft,
  FaPhone, FaEnvelope, FaMapMarkerAlt,
  FaFacebookF, FaTwitter, FaInstagram, FaTiktok
} from 'react-icons/fa';
import programsData from '../programsData';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { TimeContext } from '../TimeContext';
import { trackInterestLead, trackContactClick } from '../analytics/events';
import { resetConsent } from '../analytics/consent';
import './Homepage.css';

/* ─── Slide hook (unchanged) ─── */
const useSlideshow = (slides, interval = 5000) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused || slides.length <= 1) return;
    const timer = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % slides.length);
    }, interval);
    return () => clearInterval(timer);
  }, [isPaused, slides.length, interval]);

  const handleInteraction = useCallback(() => {
    setIsPaused(true);
    const resumeTimer = setTimeout(() => setIsPaused(false), 30000);
    return () => clearTimeout(resumeTimer);
  }, []);

  return { activeSlide, handleInteraction, setActiveSlide };
};

/* ─── Shared Header ─── */
export const SiteHeader = ({ t, i18n, navLinks }) => {
  const changeLanguage = useCallback(lng => i18n.changeLanguage(lng), [i18n]);
  const langs = [
    { code: 'en', name: 'English' },
    { code: 'sw', name: 'Swahili' },
    { code: 'fr', name: 'French' },
    { code: 'es', name: 'Spanish' },
  ];

  return (
    <header className="sttc-header">
      <div className="container">
        <Link to="/" className="sttc-logo" aria-label="San Tomas Tourism College — Home">
          <img
            src="/logo.png" alt="San Tomas Tourism College logo"
            width="56" height="56" loading="eager" fetchpriority="high" decoding="async"
          />
          <div className="sttc-logo-text">
            <h1>San Tomas Tourism College</h1>
            <p>Illuminatio et Salus Populi</p>
          </div>
        </Link>

        <nav className="sttc-nav" aria-label="Main navigation">
          {navLinks.map(({ to, label, ariaLabel }) => (
            <Link
              key={to} to={to}
              className={`sttc-nav-link${window.location.pathname === to ? ' active' : ''}`}
              aria-current={window.location.pathname === to ? 'page' : undefined}
              aria-label={ariaLabel}
            >
              {label}
            </Link>
          ))}
          <div style={{ display: 'flex', gap: 4, marginLeft: 8, alignItems: 'center' }}>
            {langs.map(({ code, name }) => (
              <button
                key={code}
                onClick={() => changeLanguage(code)}
                className={`lang-btn${i18n.language === code ? ' active' : ''}`}
                aria-label={`Switch to ${name}`}
                aria-pressed={i18n.language === code}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
};

/* ─── Shared Footer ─── */
export const SiteFooter = ({ t, currentTime }) => (
  <>
    <div className="sttc-stripe" />
    <footer className="sttc-footer">
      <div className="sttc-container">
        <div className="footer-grid">
          <div className="footer-brand">
            <h3>San Tomas Tourism College</h3>
            <p className="motto">Illuminatio et Salus Populi</p>
            <p>{t('footer.description', 'Premier tourism education since 1997, shaping global leaders in hospitality, business, IT, and languages.')}</p>
            <div className="footer-socials">
              {[
                { href: 'https://www.facebook.com/share/1A31RHo7D6/', icon: <FaFacebookF />, label: 'Facebook' },
                { href: 'https://x.com/santomascollege', icon: <FaTwitter />, label: 'Twitter' },
                { href: 'https://www.instagram.com/santomascollege?igsh=MWRhczZ5eWR1bnE4MQ==', icon: <FaInstagram />, label: 'Instagram' },
                { href: 'https://www.tiktok.com/@santomastourismcollege', icon: <FaTiktok />, label: 'TikTok' },
              ].map(s => (
                <a key={s.label} href={s.href} className="social-btn" aria-label={s.label} target="_blank" rel="noopener noreferrer">
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="footer-col-title">Quick Links</div>
            {[['/', t('nav.home','Home')], ['/programs',t('nav.programs','Programs')], ['/campus-life',t('nav.campusLife','Campus Life')], ['/alumni',t('nav.alumni','Alumni')], ['/enrollment',t('nav.enrollment','Enrollment')]].map(([to, label]) => (
              <Link key={to} to={to} className="footer-link">{label}</Link>
            ))}
            <Link to="/privacy" className="footer-link">{t('footer.privacy','Privacy notice')}</Link>
            {/* The Data Protection Act requires withdrawing consent to be as
                easy as giving it. Clears the stored choice and brings the
                banner back with no reload. */}
            <button
              type="button"
              className="footer-link"
              onClick={resetConsent}
              style={{ background:'none', border:0, padding:0, cursor:'pointer', textAlign:'left', font:'inherit' }}
            >
              {t('footer.cookieSettings','Cookie settings')}
            </button>
          </div>

          <div>
            <div className="footer-col-title">{t('footer.contact','Contact')}</div>
            <p className="footer-link" style={{ textDecoration: 'none' }}>{t('footer.address','Information House, 2nd Floor, Nairobi CBD')}</p>
            <a href="tel:+254722821836" className="footer-link" onClick={() => trackContactClick('phone')}>+254 722 821836</a>
            <a href="tel:+254728557291" className="footer-link" onClick={() => trackContactClick('phone')}>+254 728 557291</a>
            <a href="mailto:info@santomastourismcollege.com" className="footer-link" onClick={() => trackContactClick('email')}>info@santomastourismcollege.com</a>
          </div>

          <div>
            <div className="footer-col-title">{t('footer.hours','Nairobi Time')}</div>
            <p className="footer-time">{currentTime || '—'}</p>
          </div>
        </div>

        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} {t('footer.copyright','San Tomas Tourism College. All rights reserved.')}</p>
          <Link
            to="/admin"
            className="btn-primary"
            style={{ fontSize: 11, padding: '8px 18px' }}
            aria-label={t('footer.adminAria','Access admin dashboard')}
          >
            <span>{t('footer.admin','Admin Dashboard')}</span>
            <FaArrowRight />
          </Link>
        </div>
      </div>
    </footer>
  </>
);

/* ════════════════════════════════════════════
   HOMEPAGE
════════════════════════════════════════════ */
const Homepage = () => {
  const { t, i18n } = useTranslation();
  const currentTime = useContext(TimeContext);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'https://santomas-backend.up.railway.app';

  /* ── Data (all unchanged) ── */
  const facultyMembers = useMemo(() => [
    { id:1, name:'Prof. J.T Chibeu, PhD', position:'Senior Lecturer', quote:'Professor Chibeu brings over 25 years of experience in language and cultural studies. His research focuses on cross-cultural communication in tourism.', image:'/faculty/chibeu.jpg' },
    { id:3, name:'Isabella Mueni',        position:'Lecturer',          quote:'Corporate strategy expert with a focus on tourism business models. Develops practical case studies from African tourism markets.', image:'/faculty/mueni.jpg' },
    { id:4, name:'Michael W. Makokha',    position:'Head of IT',        quote:'Technology innovator bridging IS solutions with tourism operations.', image:'/faculty/makokha.jpg' },
    { id:5, name:'Norah Ngii',            position:'Head of Communication', quote:'Brand communication strategist with expertise in tourism marketing.', image:'/faculty/ngii.jpg' },
    { id:6, name:'Robert Musyoka',        position:'IS Strategist | Public Policy Analyst', quote:'From code to policy briefs: Robert\'s dual expertise drives sustainable digital transformation.', image:'/faculty/musyoka.jpg' },
  ], []);

  const heroSlides = useMemo(() => [
    { image:'https://picsum.photos/1600/900?random=1', title: t('hero.title1','Discover Your Future'),    subtitle: t('hero.subtitle1','Join a vibrant community at San Tomas.') },
    { image:'https://picsum.photos/1600/900?random=2', title: t('hero.title2','Learn with Passion'),     subtitle: t('hero.subtitle2','Explore our diverse programs.') },
    { image:'https://picsum.photos/1600/900?random=3', title: t('hero.title3','Succeed Globally'),       subtitle: t('hero.subtitle3','Our alumni thrive worldwide.') },
  ], [t]);

  const navLinks = useMemo(() => [
    { to:'/',            label: t('nav.home','Home'),        ariaLabel: t('nav.home','Home') },
    { to:'/programs',    label: t('nav.programs','Programs'),ariaLabel: t('nav.programs','Programs') },
    { to:'/campus-life', label: t('nav.campusLife','Campus Life'), ariaLabel: t('nav.campusLife','Campus Life') },
    { to:'/alumni',      label: t('nav.alumni','Alumni'),    ariaLabel: t('nav.alumni','Alumni') },
    { to:'/enrollment',  label: t('nav.enrollment','Enrollment'), ariaLabel: t('nav.enrollment','Enrollment') },
  ], [t]);

  const { activeSlide, handleInteraction, setActiveSlide } = useSlideshow(heroSlides);

  useEffect(() => {
    const prioritized = [
      ...programsData.filter(p => [31].includes(p.id)),
      ...programsData.filter(p => ![31,32,33,34].includes(p.id)).slice(0, 5),
    ];
    setCourses(prioritized);
    setLoading(false);
  }, []);

  const expressInterestSchema = useMemo(() => Yup.object({
    name: Yup.string().required(t('formErrors.nameRequired','Name is required')),
    telephone: Yup.string().required(t('formErrors.telephoneRequired','Telephone is required')).matches(/^\+254[17]\d{8}$/, t('formErrors.telephoneFormat','Format: +2547XXXXXXXX or +2541XXXXXXXX')),
    email: Yup.string().email(t('formErrors.emailInvalid','Invalid email')).required(t('formErrors.emailRequired','Email is required')),
  }), [t]);

  const handleSubmit = useCallback(async (values, { setSubmitting, setFieldError, resetForm }) => {
    try {
      await axios.post(`${API_URL}/api/express-interest`, { name: values.name, telephone: values.telephone, email: values.email }, {
        headers: { 'Content-Type': 'application/json' }, timeout: 10000,
      });
      /* Fired only after the API accepts the enquiry, so Meta counts
         real leads rather than button presses. */
      trackInterestLead('homepage_express_interest');
      setSubmissionSuccess(true);
      resetForm();
      setTimeout(() => setSubmissionSuccess(false), 5000);
    } catch (error) {
      setFieldError('general', error.response?.data?.message || t('form.error','Failed to submit. Please try again later.'));
    } finally { setSubmitting(false); }
  }, [API_URL, t]);

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{t('seo.title','San Tomas Tourism College - Premier Education in Nairobi')}</title>
        <meta name="description" content={t('seo.description','Join San Tomas Tourism College in Nairobi for top-tier courses in Language, Hospitality, Business, and IT.')} />
        <meta name="keywords" content={t('seo.keywords','San Tomas Tourism College, Nairobi college, hospitality courses, language school, business diploma, IT certification')} />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&family=Fraunces:ital,wght@0,700;0,900;1,700;1,900&display=swap" rel="stylesheet" />
        <link rel="canonical" href="https://santomastourismcollege.com/" />
        <meta property="og:title" content={t('seo.ogTitle','San Tomas Tourism College - Premier Education in Nairobi')} />
        <meta property="og:description" content={t('seo.ogDescription','Top-tier tourism education with industry-focused programs in Nairobi')} />
        <meta property="og:image" content="/social-banner.jpg" />
        <meta property="og:url" content="https://santomastourismcollege.com/" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Skip nav */}
      <a href="#main-content" className="sr-only focus:not-sr-only">
        {t('a11y.skipToContent','Skip to main content')}
      </a>

      {/* Floating CTA */}
      <motion.div
        style={{ position:'fixed', bottom:24, right:24, zIndex:999 }}
        animate={{ scale:[1,1.04,1] }}
        transition={{ repeat:Infinity, duration:2 }}
      >
        <Link to="/enrollment" className="floating-cta" aria-label={t('hero.applyAria','Apply to San Tomas Tourism College')}>
          <span>{t('hero.apply','Apply Today')}</span>
          <FaArrowRight />
        </Link>
      </motion.div>

      {/* ══ HEADER ══ */}
      <SiteHeader t={t} i18n={i18n} navLinks={navLinks} />

      {/* ══ HERO ══ */}
      <section
        id="main-content"
        className="sttc-hero"
        aria-roledescription="carousel"
        aria-label={t('a11y.campusSlideshow','Campus highlights slideshow')}
        onMouseEnter={handleInteraction}
        onTouchStart={handleInteraction}
      >
        {/* Decorative layers */}
        <div className="hero-grid" aria-hidden="true" />
        <div className="hero-geo" aria-hidden="true" />
        <div className="hero-circle-1" aria-hidden="true" />
        <div className="hero-circle-2" aria-hidden="true" />

        {/* Background slide images */}
        <div style={{ position:'absolute', inset:0, zIndex:0 }}>
          {heroSlides.map((slide, idx) => (
            <motion.div
              key={idx}
              style={{ position:'absolute', inset:0 }}
              initial={{ opacity:0 }}
              animate={{ opacity: activeSlide===idx ? 1 : 0 }}
              transition={{ duration:1.4, ease:'easeInOut' }}
              role="group" aria-roledescription="slide"
              aria-label={`Slide ${idx+1} of ${heroSlides.length}`}
              aria-hidden={activeSlide!==idx}
            >
              <img
                src={slide.image} alt={slide.title}
                style={{ width:'100%', height:'100%', objectFit:'cover', opacity:0.18 }}
                loading={idx===0?'eager':'lazy'}
                fetchpriority={idx===0?'high':'low'}
                decoding="async"
                onError={e=>{ e.target.style.display='none'; }}
              />
            </motion.div>
          ))}
        </div>

        {/* Hero content */}
        <div className="hero-content" style={{ position:'relative', zIndex:2 }}>
          <motion.div initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.8, ease:'easeOut' }}>
            <motion.div
              className="hero-tagline"
              initial={{ scale:0, opacity:0 }}
              animate={{ scale:1, opacity:1 }}
              transition={{ delay:0.2, type:'spring', stiffness:120 }}
            >
              {t('hero.tagline','Premier Tourism Education Since 1997')}
            </motion.div>

            {heroSlides.map((slide, idx) => (
              <div key={idx} style={{ display: activeSlide===idx ? 'block' : 'none' }}>
                <motion.h2
                  className="hero-h1"
                  initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
                  transition={{ delay:0.4, duration:0.7 }}
                >
                  {t('hero.title','Shape Your Future in')}
                  <span className="hero-accent">{t('hero.subtitle','Global Tourism & Hospitality')}</span>
                </motion.h2>
              </div>
            ))}

            <motion.p
              className="hero-sub"
              initial={{ opacity:0 }} animate={{ opacity:1 }}
              transition={{ delay:0.6, duration:0.8 }}
            >
              {t('hero.description','Join Kenya\'s leading tourism college for internationally recognised qualifications.')}
            </motion.p>

            <motion.div
              className="hero-stats"
              initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
              transition={{ delay:0.75, duration:0.7 }}
            >
              <div className="hero-stat">
                <FaStar style={{ color:'#9ADB02' }} />
                <span>{t('hero.stat1','95% Graduate Employment')}</span>
              </div>
              <div className="hero-stat">
                <FaHeart style={{ color:'#9ADB02' }} />
                <span>{t('hero.stat2','International Certifications')}</span>
              </div>
            </motion.div>

            <motion.div
              className="hero-ctas"
              initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
              transition={{ delay:0.9, duration:0.7 }}
            >
              <Link to="/enrollment" className="btn-primary" aria-label={t('hero.applyAria','Apply now')}>
                <span>{t('hero.apply','Apply Today')}</span>
                <FaArrowRight />
              </Link>
              <Link to="/programs" className="btn-secondary" aria-label={t('hero.exploreAria','Explore Programs')}>
                <span>{t('hero.explore','Explore Programs')}</span>
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Slide dots */}
        <div className="hero-dots">
          {heroSlides.map((_, idx) => (
            <button
              key={idx}
              className={`hero-dot${activeSlide===idx ? ' active' : ''}`}
              onClick={() => { setActiveSlide(idx); handleInteraction(); }}
              aria-label={t('a11y.goToSlide',{index:idx+1, defaultValue:`Go to slide ${idx+1}`})}
              aria-current={activeSlide===idx}
            />
          ))}
        </div>
      </section>

      {/* STRIPE */}
      <div className="sttc-stripe" />

      {/* ══ MISSION / VISION / VALUES ══ */}
      <section className="sttc-section white" aria-labelledby="purpose-heading">
        <div className="sttc-container">
          <div className="text-center mb-8">
            <div className="section-eyebrow" style={{ justifyContent:'center' }}>Our Foundation</div>
            <h2 id="purpose-heading" className="section-title">
              {t('purpose.title','Our ')} <em>Purpose</em>
            </h2>
          </div>
          <div className="purpose-grid">
            {[
              { cls:'mission', icon:<FaLightbulb/>, key:'mission', defaultH:'Mission', defaultP:'To provide knowledge, skills, and competence in Hospitality, Business Studies, IT, and Foreign Languages.' },
              { cls:'vision',  icon:<FaStar/>,      key:'vision',  defaultH:'Vision',  defaultP:'High-quality training for academic excellence and professional development.' },
              { cls:'values',  icon:<FaHeart/>,     key:'values',  defaultH:'Core Values', defaultP:'Honesty, transparency, good governance, integrity, respect, and commitment.' },
            ].map((card, i) => (
              <motion.div
                key={card.key}
                className={`purpose-card ${card.cls}`}
                initial={{ y:50, opacity:0 }} whileInView={{ y:0, opacity:1 }}
                viewport={{ once:true, margin:'-50px' }}
                transition={{ duration:0.6, delay: i*0.15 }}
                whileHover={{ scale:1.02 }}
                tabIndex="0"
              >
                <div className="purpose-icon">{card.icon}</div>
                <h3>{t(`purpose.${card.key}`, card.defaultH)}</h3>
                <p>{t(`purpose.${card.key}Text`, card.defaultP)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STRIPE */}
      <div className="sttc-stripe" />

      {/* ══ FEATURED PROGRAMS ══ */}
      <section className="sttc-section tinted" aria-labelledby="programs-heading">
        <div className="sttc-container">
          <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'space-between', alignItems:'flex-end', marginBottom:48, gap:20 }}>
            <div>
              <div className="section-eyebrow">What We Offer</div>
              <h2 id="programs-heading" className="section-title">
                Featured <em>Programs</em>
              </h2>
            </div>
            <Link to="/programs" className="btn-outline" aria-label={t('a11y.viewAllPrograms','View all programs')}>
              <span>{t('courses.viewAll','View All Programs')}</span>
              <FaArrowRight />
            </Link>
          </div>

          {loading ? (
            <div style={{ display:'flex', justifyContent:'center', padding:'48px 0' }}>
              <div style={{ width:40, height:40, borderRadius:'50%', border:'3px solid rgba(65,105,225,0.15)', borderTopColor:'#4169E1', animation:'spin 0.8s linear infinite' }} />
            </div>
          ) : (
            <div className="programs-grid">
              {courses.map(course => (
                <motion.div
                  key={course.id}
                  className="program-card"
                  initial={{ opacity:0, y:50 }} whileInView={{ opacity:1, y:0 }}
                  viewport={{ once:true, margin:'-50px' }}
                  transition={{ duration:0.6 }}
                  tabIndex="0"
                >
                  <div className="program-card-header">
                    <div className="program-card-icon">
                      <img
                        src={`https://picsum.photos/32/32?random=${course.id}`}
                        alt={`${course.name} icon`}
                        loading="lazy" decoding="async"
                        onError={e => e.target.parentNode.classList.add('hidden')}
                      />
                    </div>
                    <h3>{course.name}</h3>
                  </div>
                  <div className="program-card-body">
                    <p>{course.description}</p>
                    <div className="program-duration">
                      <span>{t('courses.duration','Duration:')} {course.duration}</span>
                    </div>
                    <Link
                      to={`/programs/${course.id}`}
                      className="program-card-link"
                      aria-label={t('courses.learnMoreAria',{ name:course.name, defaultValue:`Learn more about ${course.name}` })}
                    >
                      {t('courses.learnMore','Learn more')} <FaArrowRight />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* STRIPE */}
      <div className="sttc-stripe" />

      {/* ══ FACULTY SPOTLIGHTS ══ */}
      <section className="sttc-section faculty-section-bg" aria-labelledby="faculty-heading">
        <div className="sttc-container">
          <div className="text-center mb-8">
            <div className="section-eyebrow">Meet the Team</div>
            <h2 id="faculty-heading" className="section-title">
              Faculty <em>Spotlights</em>
            </h2>
          </div>
          <div className="faculty-grid">
            {facultyMembers.map((lecturer, idx) => (
              <motion.div
                key={lecturer.id}
                className="faculty-card"
                initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }}
                viewport={{ once:true }}
                transition={{ duration:0.6, delay: idx*0.1 }}
                whileHover={{ scale:1.02 }}
                tabIndex="0"
              >
                <div className="faculty-photo-ring">
                  <img
                    src={lecturer.image}
                    alt={`${lecturer.name}, ${lecturer.position}`}
                    width="88" height="88"
                    loading="lazy" decoding="async"
                    onError={e=>{ e.target.src='/images/faculty-placeholder.jpg'; }}
                  />
                  <div className="faculty-quote-badge">
                    <FaQuoteLeft />
                  </div>
                </div>
                <h4>{lecturer.name}</h4>
                <p className="position">{lecturer.position}</p>
                <p className="quote">{lecturer.quote}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STRIPE */}
      <div className="sttc-stripe" />

      {/* ══ ALUMNI TEASER ══ */}
      <section className="sttc-section alumni-teaser" aria-labelledby="alumni-heading">
        <div className="sttc-container">
          <div style={{ maxWidth:680, margin:'0 auto', textAlign:'center' }}>
            <div className="section-eyebrow" style={{ justifyContent:'center' }}>Our Community</div>
            <h2 id="alumni-heading" className="section-title mb-4">
              {t('alumni.title','Alumni')} <em style={{ color:'#1E3A8A' }}>Network</em>
            </h2>
            <p style={{ fontSize:15, color:'rgba(30,58,138,0.8)', lineHeight:1.8, marginBottom:36, fontWeight:400 }}>
              {t('alumni.description','Our alumni network spans the globe, with graduates excelling in hospitality, business, IT, and language fields. Inspired by our motto "Illuminatio et Salus Populi," they drive positive change worldwide.')}
            </p>
            <motion.div whileHover={{ scale:1.04 }} whileTap={{ scale:0.96 }}>
              <Link to="/alumni" className="btn-royal" aria-label={t('a11y.joinAlumni','Join our alumni community')}>
                <span>{t('alumni.join','Join Our Community')}</span>
                <FaArrowRight />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STRIPE */}
      <div className="sttc-stripe" />

      {/* ══ CONTACT + EXPRESS INTEREST ══ */}
      <section className="sttc-section contact-section" aria-labelledby="contact-heading">
        <div className="sttc-container">
          <div className="text-center mb-8">
            <div className="section-eyebrow" style={{ justifyContent:'center' }}>Reach Out</div>
            <h2 id="contact-heading" className="section-title">
              {t('contact.title','Contact ')} <em>Us</em>
            </h2>
          </div>

          <div className="contact-cards">
            {[
              { icon:<FaPhone/>, titleKey:'phone', title:'Phone', content:'+254722821836, +254728557291' },
              { icon:<FaMapMarkerAlt/>, titleKey:'address', title:'Address', content:'Information House, 2nd Floor, Nairobi CBD' },
              { icon:<FaEnvelope/>, titleKey:'email', title:'Email', content:'info@santomastourismcollege.com' },
            ].map(card => (
              <div key={card.titleKey} className="contact-card">
                <div className="contact-card-icon">{card.icon}</div>
                <div>
                  <h3>{t(`contact.${card.titleKey}`, card.title)}</h3>
                  <p>{card.content}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Express Interest Form */}
          {submissionSuccess ? (
            <div className="form-success" style={{ maxWidth:520, margin:'0 auto' }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🎉</div>
              <h4>{t('form.success','Thank you for your interest!')}</h4>
              <p>{t('form.successDetails','Our admissions team will contact you shortly.')}</p>
              <button
                onClick={() => setSubmissionSuccess(false)}
                className="btn-outline" style={{ marginTop:16 }}
                aria-label={t('form.submitAnother','Submit another response')}
              >
                {t('form.submitAnother','Submit another response')}
              </button>
            </div>
          ) : (
            <div className="interest-form-card">
              <h3>{t('contact.interestTitle','Express Your Interest')}</h3>
              <p className="sub">{t('contact.interestText','Interested in joining us? Leave your details and we\'ll get in touch!')}</p>
              <Formik
                initialValues={{ name:'', telephone:'', email:'' }}
                validationSchema={expressInterestSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, errors, touched }) => (
                  <Form>
                    <div className="form-field">
                      <label htmlFor="name" className="form-label">{t('form.name','Name')} *</label>
                      <Field
                        type="text" name="name" id="name"
                        className={`form-input${errors.name && touched.name ? ' error' : ''}`}
                        aria-required="true"
                        aria-invalid={errors.name && touched.name ? 'true' : 'false'}
                        aria-describedby={errors.name && touched.name ? 'name-error' : undefined}
                      />
                      <ErrorMessage name="name" component="div" id="name-error" className="form-error" />
                    </div>

                    <div className="form-field">
                      <label htmlFor="telephone" className="form-label">{t('form.telephone','Telephone')} *</label>
                      <Field
                        type="tel" name="telephone" id="telephone"
                        placeholder="+2547XXXXXXXX or +2541XXXXXXXX"
                        className={`form-input${errors.telephone && touched.telephone ? ' error' : ''}`}
                        aria-required="true"
                        aria-invalid={errors.telephone && touched.telephone ? 'true' : 'false'}
                        aria-describedby={errors.telephone && touched.telephone ? 'telephone-error' : undefined}
                      />
                      <ErrorMessage name="telephone" component="div" id="telephone-error" className="form-error" />
                    </div>

                    <div className="form-field">
                      <label htmlFor="email" className="form-label">{t('form.email','Email')} *</label>
                      <Field
                        type="email" name="email" id="email"
                        className={`form-input${errors.email && touched.email ? ' error' : ''}`}
                        aria-required="true"
                        aria-invalid={errors.email && touched.email ? 'true' : 'false'}
                        aria-describedby={errors.email && touched.email ? 'email-error' : undefined}
                      />
                      <ErrorMessage name="email" component="div" id="email-error" className="form-error" />
                    </div>

                    {errors.general && (
                      <div style={{ color:'#EF4444', fontSize:13, textAlign:'center', marginBottom:12 }}>
                        {errors.general}
                      </div>
                    )}

                    <motion.button
                      type="submit" disabled={isSubmitting}
                      className="btn-primary"
                      style={{ width:'100%', justifyContent:'center', border:'none', fontSize:13, padding:'13px 20px' }}
                      aria-label={t('form.submitInterest','Submit interest form')}
                      whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
                    >
                      {isSubmitting ? t('form.submitting','Submitting...') : t('form.submit','Submit Interest')}
                      {!isSubmitting && <FaArrowRight />}
                    </motion.button>
                  </Form>
                )}
              </Formik>
            </div>
          )}
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <SiteFooter t={t} currentTime={currentTime} />
    </div>
  );
};

export default Homepage;